import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiTrendingUp,
  FiInfo,
  FiHome,
  FiCalendar,
  FiDollarSign,
  FiPhone,
  FiBookOpen,
  FiMenu,
  FiX
} = FiIcons;

const Navbar = ({ onOpenGuide }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <motion.nav
      className="bg-white shadow-lg border-b border-indigo-100 sticky top-0 z-40"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0" onClick={closeMobileMenu}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-1.5 sm:p-2 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="text-white text-lg sm:text-xl" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-none">iNVESTT</h1>
              <p className="text-[9px] sm:text-[10px] text-indigo-600 font-semibold tracking-wide">
                SEBI Reg. INH000010858
              </p>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className="text-gray-600 hover:text-indigo-600 font-medium text-sm transition-colors flex items-center space-x-1"
            >
              <SafeIcon icon={FiHome} />
              <span>Home</span>
            </Link>
            <button
              onClick={onOpenGuide}
              className="text-gray-600 hover:text-indigo-600 font-medium text-sm transition-colors flex items-center space-x-1 focus:outline-none"
            >
              <SafeIcon icon={FiBookOpen} />
              <span>Guide</span>
            </button>
            <Link
              to="/about"
              className="text-gray-600 hover:text-indigo-600 font-medium text-sm transition-colors flex items-center space-x-1"
            >
              <SafeIcon icon={FiInfo} />
              <span>About</span>
            </Link>
            <a
              href="https://www.investt.in/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-indigo-600 font-medium text-sm transition-colors flex items-center space-x-1"
            >
              <SafeIcon icon={FiDollarSign} />
              <span>Pricing</span>
            </a>
            <a
              href="https://www.investt.in/appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-indigo-600 font-medium text-sm transition-colors flex items-center space-x-1"
            >
              <SafeIcon icon={FiCalendar} />
              <span>Appointment</span>
            </a>
            <a
              href="https://www.investt.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg text-sm font-medium"
            >
              <SafeIcon icon={FiPhone} className="text-sm" />
              <span>Contact Us</span>
            </a>
          </div>

          {/* Mobile Menu Toggle & Guide Button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={onOpenGuide}
              className="text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Open Guide"
            >
              <SafeIcon icon={FiBookOpen} className="text-xl" />
            </button>
            <button
              onClick={toggleMobileMenu}
              className="text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Toggle Menu"
            >
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-3">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiHome} className="text-lg" />
                <span className="font-medium">Home</span>
              </Link>
              <button
                onClick={() => {
                  onOpenGuide();
                  closeMobileMenu();
                }}
                className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors w-full text-left"
              >
                <SafeIcon icon={FiBookOpen} className="text-lg" />
                <span className="font-medium">Guide</span>
              </button>
              <Link
                to="/about"
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiInfo} className="text-lg" />
                <span className="font-medium">About</span>
              </Link>
              <a
                href="https://www.investt.in/pricing"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiDollarSign} className="text-lg" />
                <span className="font-medium">Pricing</span>
              </a>
              <a
                href="https://www.investt.in/appointment"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiCalendar} className="text-lg" />
                <span className="font-medium">Book Appointment</span>
              </a>
              <a
                href="https://www.investt.in"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center justify-center space-x-2 bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors shadow-md font-medium mt-2"
              >
                <SafeIcon icon={FiPhone} />
                <span>Contact Us</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;